import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { projectsAPI } from '../services/api';

function ProjectForm() {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(isEditMode);
  const navigate = useNavigate();

  useEffect(() => {
    if (isEditMode) {
      fetchProject();
    }
  }, [id]);

  const fetchProject = async () => {
    try {
      setFetchingData(true);
      const response = await projectsAPI.getProject(id);
      const project = response.data;
      setFormData({
        name: project.name,
        description: project.description || '',
        is_active: project.is_active
      });
    } catch (error) {
      toast.error('Failed to load project');
      console.error(error);
      navigate('/projects');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData({
      ...formData,
      [name]: newValue
    });
  };

  // Generate project key from name
  const generateKey = (name) => {
    return name
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '') // Remove special characters
      .split(/\s+/) // Split by spaces
      .map(word => word.substring(0, 3)) // Take first 3 letters of each word
      .join('-')
      .substring(0, 10); // Limit to 10 characters
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Project name is required');
      return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        // Update existing project
        await projectsAPI.updateProject(id, formData);
        toast.success('Project updated successfully!');
      } else {
        // Create new project with auto-generated key
        const projectKey = generateKey(formData.name);
        await projectsAPI.createProject({
          ...formData,
          key: projectKey
        });
        toast.success('Project created successfully!');
      }

      navigate('/projects');
    } catch (error) {
      toast.error(isEditMode ? 'Failed to update project' : 'Failed to create project');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <Container fluid style={{ padding: '2rem 3rem' }}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid style={{ padding: '2rem 3rem' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="fw-bold mb-0" style={{ fontSize: '28px', color: '#1a1a1a' }}>
          {isEditMode ? 'Edit Project' : 'Create Project'}
        </h2>
        <Button
          variant="outline-secondary"
          onClick={() => navigate('/projects')}
          style={{
            borderRadius: '8px',
            padding: '10px 20px',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          Cancel
        </Button>
      </div>

      <Card className="border-0 shadow-sm" style={{ borderRadius: '10px', maxWidth: '800px' }}>
        <Card.Body style={{ padding: '2rem' }}>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '8px' }}>
                    Project Name <span style={{ color: '#dc3545' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter project name"
                    required
                    style={{
                      borderRadius: '8px',
                      padding: '10px 14px',
                      fontSize: '14px',
                      border: '1px solid #e0e0e0'
                    }}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '12px' }}>
                    Project key will be auto-generated from the name
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '8px' }}>
                    Description
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter project description"
                    rows={4}
                    style={{
                      borderRadius: '8px',
                      padding: '10px 14px',
                      fontSize: '14px',
                      border: '1px solid #e0e0e0'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={12}>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    label="Active"
                    style={{ fontSize: '14px', color: '#333' }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex gap-2">
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: '600',
                  fontSize: '14px',
                  border: 'none',
                  minWidth: '120px'
                }}
              >
                {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Project' : 'Create Project')}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate('/projects')}
                disabled={loading}
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default ProjectForm;
